import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { Ranking } from '@flipster/apis/v2/trading-competition/types';
import { FormattedMessage } from '@flipster/i18n';
import { useRouter } from '@flipster/i18n/routing';
import Formatted from '@flipster/react/design-utils/formatted';
import ChevronRightIcon from '@flipster/react/icon/generated/ChevronRightIcon';
import LockClosedIcon from '@flipster/react/icon/generated/LockClosedIcon';
import { useBreakpoint } from '@flipster/react/misc/responsive/index';
import { css } from '@flipster/styled-system/css';
import { center, flex, hstack, vstack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import { UsdtCurrency } from '@flipster/utils/constants/currency';
import webviewHandler from '@flipster/utils/webview-handler';

import { ImgOrLottie, contentfulImageUrl } from '../../../contentful/assets';
import { EventPeriod } from '../component/event-period';
import { SeparatorTitle } from '../component/separator-title';
import { topRankLayout } from '../misc/rank';
import { getTopRankItemStyle } from '../misc/style';
import { competitionBunja, competitionEventsBunja, competitionTabsBunja } from '../state';
import { competitionRankingsBunja } from './leaderboard/state';

interface RankRewardProps {
  rankings: Ranking[];
}

const TopRankList = ({ rankings }: RankRewardProps) => {
  const { competitionContentful } = useBunja(competitionBunja);
  const { selectedEventTabValueAtom } = useBunja(competitionTabsBunja);
  const selectedEventTabValue = useAtomValue(selectedEventTabValueAtom);
  const router = useRouter();
  const { isMobile } = useBreakpoint();

  const isRoi = selectedEventTabValue === 'roi';

  const getIconUrl = (rank: number) => {
    const url =
      rank === 1
        ? competitionContentful?.rank1Icon?.url
        : rank === 2
          ? competitionContentful?.rank2Icon?.url
          : competitionContentful?.rank3Icon?.url;
    return contentfulImageUrl(url ?? '');
  };

  return (
    <ul className={flex({ direction: { base: 'column', tablet: 'row' }, gap: { base: 0, tablet: 24 }, w: '100%' })}>
      {topRankLayout(rankings.length, isMobile).map((rank) => {
        const ranking = rankings[rank - 1];
        const { prize, nickname, profileVisibility, userProfileId, value } = ranking;
        return (
          <li className={getTopRankItemStyle(rank)} key={nickname}>
            <ImgOrLottie
              url={getIconUrl(rank)}
              alt={`rank${rank}icon`}
              css={{ w: rank > 1 ? 120 : 160, h: rank > 1 ? 120 : 160 }}
            />
            <div className={vstack({ gap: 8 })}>
              <p className={css({ textStyle: 'Title/M', color: 'content.default.level-5', textAlign: 'center' })}>
                <FormattedMessage id='Competition.r251215.Top_rank' values={{ rank: rank }} />
              </p>
              <p
                className={center({
                  textStyle: 'Title/S',
                  color: 'content.default.level-5',
                  textAlign: 'right',
                  cursor: 'pointer',
                  gap: 4,
                })}
                onClick={() => {
                  webviewHandler.postMessage(
                    {
                      action: 'link',
                      params: {
                        page: `user/profile/${userProfileId}`,
                      },
                    },
                    () => router.push(`/user/profile/${userProfileId}`),
                  );
                }}
              >
                <span>{nickname}</span>
                {profileVisibility === 'PRIVATE' && (
                  <LockClosedIcon size={24} color={token('colors.content.default.level-5')} />
                )}
                {profileVisibility === 'PUBLIC' && (
                  <ChevronRightIcon size={24} color={token('colors.content.default.level-5')} />
                )}
              </p>
            </div>
            <dl className={vstack({ w: '100%', gap: 8 })}>
              <div className={hstack({ w: '100%', gap: 8 })}>
                <dt className={dtStyle}>
                  <FormattedMessage id='Autogen_common.r251126.s_Prize' />
                </dt>
                <dd className={ddStyle}>
                  <Formatted.Number
                    value={prize}
                    css={{ textStyle: 'Title/XS', color: 'var(--key-color)' }}
                    suffix={UsdtCurrency}
                  />
                </dd>
              </div>
              <div className={hstack({ w: '100%', gap: 8 })}>
                <dt className={dtStyle}>
                  {selectedEventTabValue === 'volume' ? (
                    <FormattedMessage id='Common.r250923.Volume' />
                  ) : isRoi ? (
                    <FormattedMessage id='Common.r251127.Roi' />
                  ) : (
                    <FormattedMessage id='Autogen_common.r251126.s_PnL' />
                  )}
                </dt>
                <dd className={ddStyle}>
                  {isRoi ? (
                    <Formatted.Percent
                      value={value}
                      isRate
                      toFixed
                      css={{ textStyle: 'Title/XS', color: 'content.default.level-5' }}
                    />
                  ) : (
                    <Formatted.Number
                      value={value}
                      toFixed
                      precision={2}
                      css={{ textStyle: 'Title/XS', color: 'content.default.level-5' }}
                      suffix={UsdtCurrency}
                    />
                  )}
                </dd>
              </div>
            </dl>
          </li>
        );
      })}
    </ul>
  );
};

export const Champions = () => {
  const { competitionAtom } = useBunja(competitionBunja);
  const { competitionEventContentfulDataAtom } = useBunja(competitionEventsBunja);
  const { rankingsAtom } = useBunja(competitionRankingsBunja);

  const competitionEventContentful = useAtomValue(competitionEventContentfulDataAtom);
  const competition = useAtomValue(competitionAtom);
  const rankings = useAtomValue(rankingsAtom);

  if (!competitionEventContentful || !competition || !rankings?.length) return null;

  const top3Rankings = rankings.slice(0, 3);

  return (
    <section className={vstack({ py: 24 })}>
      <SeparatorTitle
        colorSet='normal'
        title={<>{competitionEventContentful?.label} Champions</>}
        description={<EventPeriod start={competition.competitionStartTime} end={competition.competitionEndTime} />}
        css={{ mb: 24 }}
      />
      <TopRankList rankings={top3Rankings} />
    </section>
  );
};

const dtStyle = css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3', textAlign: 'left' });
const ddStyle = css({ flex: 1, textStyle: 'Title/XS', color: 'content.default.level-5', textAlign: 'right' });
